import { type EndorsementValidationState } from '@orus.eu/dimensions'
import { type ProtectionStatus } from '@orus.eu/protection'
import { type Permission } from '@orus.eu/right-access-management'

export type EndorsementAvailability =
  | { type: 'hidden' }
  | { type: 'start' }
  | { type: 'continue'; endorsementId: string }

export type PendingEndorsement = { id: string; validationState: EndorsementValidationState }

export type EndorsementAvailabilityInput = {
  isManagedByOrus: boolean
  canCreateEndorsement: boolean
  currentProtectionStatus: ProtectionStatus
  pendingEndorsement: PendingEndorsement | undefined
}

export function hasEndorsementCreationPermission(permissions: readonly Permission[]): boolean {
  return permissions.includes('endorsement.create') || permissions.includes('endorsement.create.managedByOrusOnly')
}

/**
 * Single decision site for the "Modifier" action. `continue` wins over `start`: an open endorsement
 * forbids creating a second one, whatever the contract status.
 */
export function getEndorsementAvailability({
  isManagedByOrus,
  canCreateEndorsement,
  currentProtectionStatus,
  pendingEndorsement,
}: EndorsementAvailabilityInput): EndorsementAvailability {
  if (!isManagedByOrus) return { type: 'hidden' }
  if (!canCreateEndorsement) return { type: 'hidden' }
  if (isEndorsementWaitingForValidation(pendingEndorsement)) return { type: 'hidden' }
  if (pendingEndorsement) return { type: 'continue', endorsementId: pendingEndorsement.id }
  if (currentProtectionStatus === 'terminated') return { type: 'hidden' }
  return { type: 'start' }
}

export function isEndorsementWaitingForValidation(pendingEndorsement: PendingEndorsement | undefined): boolean {
  return pendingEndorsement?.validationState === 'validation_requested'
}
